import { useMemo } from 'react'

import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { appointmentStatuses } from '../../../../common/constants/appConstants'

/**
 * Maps appointment meetingFormat to the modality form field value.
 * The form expects a JSON string with format: '{"type": "video" | "in_person" | "tele_therapy"}'
 *
 * Note: For in_person, the full modality object includes state and zipcode from provider addresses,
 * so we find the matching option from modalityOptions which already contains these values.
 *
 * @param modalityOptionsEnum - Array of JSON strings from useModalityOptions().enum
 * @param meetingFormat - The appointment's meeting format
 */
const getModalityFromAppointment = (
  modalityOptionsEnum: string[],
  meetingFormat?: MEETING_FORMATS,
): string | undefined => {
  if (!meetingFormat) return undefined

  switch (meetingFormat) {
    case MEETING_FORMATS.IN_PERSON:
    case MEETING_FORMATS.IN_PERSON_UNDERSCORE: {
      const inPersonOption = modalityOptionsEnum.find((mo) => {
        try {
          return JSON.parse(mo).type === 'in_person'
        } catch {
          return false
        }
      })
      return inPersonOption
    }
    case MEETING_FORMATS.VIDEO:
      return JSON.stringify({ type: 'video' })
    case MEETING_FORMATS.PHONE:
    case MEETING_FORMATS.CALL:
      return JSON.stringify({ type: 'tele_therapy' })
    default:
      return undefined
  }
}

/**
 * Derives the original changeReason/changeNotice form values from appointment data.
 * This is for DA providers only.
 *
 * The form saves these values with transformations:
 * - changeReason='clientCancel' stores the changeNotice value in appointment.changeReason
 * - changeReason='clientInitiatedLate' (no-show) sets appointmentStatus='missed'
 * - changeReason='providerInitiated' stores details in appointmentAttributes
 *
 * This function reverses those transformations.
 *
 * Note: attendance is handled separately in useInitialEndOfSessionValues.
 *
 * @param appointment - The appointment being closed
 */
const getChangeReasonFromAppointment = (
  appointment: Appointment,
): {
  changeReason?: string
  changeNotice?: string
} => {
  const { changeReason, appointmentAttributes, appointmentStatus } = appointment

  // Attended or ineligible sessions don't have a changeReason
  if (appointmentStatus === appointmentStatuses.completed) {
    return {}
  }

  if (changeReason === attendanceValues.INELIGIBLE) {
    return {}
  }

  // For no-show/canceled scenarios, derive the changeReason
  if (appointmentAttributes?.providerInitiatedCancelSelection) {
    return { changeReason: 'providerInitiated' }
  }

  if (appointmentStatus === appointmentStatuses.missed) {
    return { changeReason: 'clientInitiatedLate' }
  }

  const changeNoticeValues = ['clientInitiated', 'clientInitiatedLate']
  if (typeof changeReason === 'string' && changeNoticeValues.includes(changeReason)) {
    return { changeReason: 'clientCancel', changeNotice: changeReason }
  }

  if (changeReason) {
    return { changeReason: changeReason as string }
  }

  return {}
}

interface InitialSessionDetails {
  changeReason?: string
  changeNotice?: string
  technicalIssuesCancelSelection?: string
  technicalIssuesCancelDetails?: string
  modality?: string
}

/**
 * Hook to derive initial end-of-session form values from appointment data.
 * This complements useInitialEndOfSessionValues which derives values from previous charges/transfers.
 *
 * @param appointment - The appointment being closed
 * @param modalityOptionsEnum - Array of JSON strings from useModalityOptions().enum
 * @returns Initial values for changeReason, changeNotice, modality fields
 */
export const useInitialSessionDetails = (
  appointment: Appointment | undefined,
  modalityOptionsEnum: string[],
): InitialSessionDetails => {
  return useMemo(() => {
    if (!appointment) return {}

    const initialValues: InitialSessionDetails = {}

    const changeReasonValues = getChangeReasonFromAppointment(appointment)
    Object.assign(initialValues, changeReasonValues)

    const modality = getModalityFromAppointment(modalityOptionsEnum, appointment.meetingFormat)
    if (modality) {
      initialValues.modality = modality
    }

    if (appointment.appointmentAttributes?.technicalIssuesCancelSelection) {
      initialValues.technicalIssuesCancelSelection = appointment.appointmentAttributes.technicalIssuesCancelSelection
    }
    if (appointment.appointmentAttributes?.technicalIssuesCancelDetails) {
      initialValues.technicalIssuesCancelDetails = appointment.appointmentAttributes.technicalIssuesCancelDetails
    }

    return initialValues
  }, [appointment, modalityOptionsEnum])
}
